/** Recurring Activity form state, validation, defaults, and cadence copy. */
import type { Difficulty, OneTimeActivity, Period, RecurringActivity } from '@life-rpg/shared';

import type { RecurringActivityDraft } from './activityRepository.js';

export interface RecurringActivityFormState {
  name: string;
  unitLabel: string;
  quickAdd: string[];
  targetQty: string;
  targetEvery: string;
  targetPer: Period;
  targetStartsOn: string;
  targetDayOfMonth: string;
  difficulty: Difficulty;
}

const localDateInput = (date: Date): string => {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const newRecurringActivityForm = (
  date: Date,
  seed?: Pick<OneTimeActivity, 'difficulty' | 'name'>,
): RecurringActivityFormState => {
  const startsOn = localDateInput(date);
  return {
    name: seed?.name ?? '',
    unitLabel: '',
    quickAdd: ['1'],
    targetQty: '1',
    targetEvery: '1',
    targetPer: seed === undefined ? 'day' : 'month',
    targetStartsOn: startsOn,
    targetDayOfMonth: String(date.getDate()),
    difficulty: seed?.difficulty ?? 'medium',
  };
};

export const editRecurringActivityForm = (
  activity: RecurringActivity,
): RecurringActivityFormState => ({
  name: activity.name,
  unitLabel: activity.unitLabel,
  quickAdd: activity.quickAdd.map(String),
  targetQty: String(activity.target.qty),
  targetEvery: String(activity.target.every),
  targetPer: activity.target.per,
  targetStartsOn: activity.target.startsOn,
  targetDayOfMonth: String(activity.target.dayOfMonth ?? 1),
  difficulty: activity.difficulty,
});

export type ParsedRecurringActivityForm =
  { draft: RecurringActivityDraft; problem?: never } | { draft?: never; problem: string };

export const parseRecurringActivityForm = (
  form: RecurringActivityFormState,
): ParsedRecurringActivityForm => {
  const name = form.name.trim();
  const unitLabel = form.unitLabel.trim();
  const quickAdd = form.quickAdd.map(Number);
  const targetQty = Number(form.targetQty);
  const targetEvery = Number(form.targetEvery);
  const dayOfMonth = Number(form.targetDayOfMonth);
  if (name === '' || unitLabel === '') {
    return { problem: 'Name and unit label cannot be blank.' };
  }
  if (quickAdd.length === 0 || quickAdd.some((value) => !Number.isInteger(value) || value <= 0)) {
    return { problem: 'Quick-add amounts must be positive whole numbers.' };
  }
  if (!Number.isInteger(targetQty) || targetQty <= 0) {
    return { problem: 'Target quantity must be a positive whole number.' };
  }
  if (!Number.isInteger(targetEvery) || targetEvery <= 0) {
    return { problem: 'Repeat interval must be a positive whole number.' };
  }
  if (
    form.targetPer === 'month' &&
    (!Number.isInteger(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31)
  ) {
    return { problem: 'Day of month must be between 1 and 31.' };
  }

  return {
    draft: {
      name,
      unitLabel,
      quickAdd,
      target: {
        qty: targetQty,
        every: targetEvery,
        per: form.targetPer,
        startsOn:
          form.targetPer === 'month'
            ? `${form.targetStartsOn.slice(0, 7)}-01`
            : form.targetStartsOn,
        ...(form.targetPer === 'month' ? { dayOfMonth } : {}),
      },
      difficulty: form.difficulty,
    },
  };
};

const INTERVAL_WORDS: Partial<Record<number, string>> = {
  2: 'two',
  3: 'three',
};

export const formatDifficultyPeriod = (every: string, per: Period): string => {
  const interval = Number(every);
  if (interval === 1) return per;
  return `${INTERVAL_WORDS[interval] ?? every}-${per} period`;
};

const formatAnchor = (activity: RecurringActivity): string => {
  const [year, month, day] = activity.target.startsOn.split('-').map(Number);
  const preferredDay = activity.target.dayOfMonth ?? day;
  const lastDay = new Date(year ?? 0, month ?? 0, 0).getDate();
  return new Date(
    year ?? 0,
    (month ?? 1) - 1,
    Math.min(preferredDay ?? 1, lastDay),
  ).toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

export const formatRecurringActivityCadence = (activity: RecurringActivity): string => {
  const period =
    activity.target.every === 1
      ? activity.target.per
      : `${activity.target.every} ${activity.target.per}s`;
  return `${activity.target.qty} ${activity.unitLabel} every ${period}, starting ${formatAnchor(activity)}`;
};
